import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Plus, Loader2, CircleDot, Clock, CheckCircle2 } from "lucide-react";
import AuditReadiness from "./AuditReadiness";
import FindingActivityLog from "./FindingActivityLog";

const SEVERITY_CLS = {
  critical: "border-red-500/40 bg-red-500/10 text-red-600",
  high: "border-orange-500/40 bg-orange-500/10 text-orange-600",
  medium: "border-amber-500/40 bg-amber-500/10 text-amber-600",
  low: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
};

const FINDING_STATUS_META = {
  open: { label: "Open", icon: CircleDot, cls: "text-red-600 border-red-500/40 bg-red-500/10" },
  in_progress: { label: "In progress", icon: Clock, cls: "text-amber-600 border-amber-500/40 bg-amber-500/10" },
  resolved: { label: "Resolved", icon: CheckCircle2, cls: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10" },
};

function AddAuditModal({ open, onClose, onSaved, frameworks }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [framework, setFramework] = useState(frameworks[0]?.key || "");
  const [type, setType] = useState("Internal");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast({ title: "Name is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/compliance/audits", { name, framework, type });
      toast({ title: "Audit planned" });
      setName("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't plan audit", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-audit-modal">
        <DialogHeader><DialogTitle>Plan an audit</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" autoFocus data-testid="compliance-audit-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Framework</Label>
              <Select value={framework} onValueChange={setFramework}>
                <SelectTrigger className="mt-1" data-testid="compliance-audit-framework-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {frameworks.map((f) => <SelectItem key={f.key} value={f.key}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1" data-testid="compliance-audit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Internal">Internal</SelectItem>
                  <SelectItem value="External">External</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-audit-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-audit-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Planning…" : "Plan audit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddFindingModal({ open, onClose, onSaved, auditId }) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [owner, setOwner] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast({ title: "Title is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post(`/compliance/audits/${auditId}/findings`, { title, severity, owner: owner || null, description: description || null });
      toast({ title: "Finding logged" });
      setTitle(""); setOwner(""); setDescription("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't log finding", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-finding-modal">
        <DialogHeader><DialogTitle>Log a finding</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" autoFocus data-testid="compliance-finding-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger className="mt-1" data-testid="compliance-finding-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Owner</Label>
              <Input value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1" data-testid="compliance-finding-owner-input" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" rows={3} data-testid="compliance-finding-description-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-finding-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-finding-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Logging…" : "Log finding"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FindingsDrawer({ audit, onClose, isAdmin, onChanged }) {
  const { toast } = useToast();
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    if (!audit) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/compliance/audits/${audit.id}/findings`);
      setFindings(data.findings || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [audit]);
  useEffect(() => { load(); }, [load]);

  if (!audit) return null;

  const updateStatus = async (finding, status) => {
    try {
      const { data } = await api.put(`/compliance/audits/${audit.id}/findings/${finding.id}`, { status });
      setFindings((prev) => prev.map((f) => (f.id === finding.id ? data : f)));
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't update finding", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  return (
    <Dialog open={!!audit} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg max-h-[75vh] overflow-y-auto" data-testid="compliance-findings-drawer">
        <DialogHeader><DialogTitle>{audit.name} — Findings</DialogTitle></DialogHeader>
        {isAdmin && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAddOpen(true)} data-testid="compliance-finding-add-btn">
              <Plus className="w-3.5 h-3.5" /> Log finding
            </Button>
          </div>
        )}
        {loading ? (
          <div className="h-24 rounded-lg bg-muted/40 animate-pulse" />
        ) : findings.length === 0 ? (
          <div className="text-[12.5px] text-muted-foreground text-center py-8">No findings logged for this audit.</div>
        ) : (
          <div className="space-y-2.5">
            {findings.map((f) => {
              const meta = FINDING_STATUS_META[f.status] || FINDING_STATUS_META.open;
              return (
                <div key={f.id} className="rounded-lg border border-border p-3" data-testid={`compliance-finding-row-${f.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-[13px] font-medium leading-snug">{f.title}</div>
                    <span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5 shrink-0", SEVERITY_CLS[f.severity])}>{f.severity}</span>
                  </div>
                  {f.description && <div className="text-[12px] text-muted-foreground mt-1.5">{f.description}</div>}
                  <div className="flex items-center justify-between mt-2.5">
                    <span className="text-[11px] text-muted-foreground">{f.owner || "Unassigned"}</span>
                    {isAdmin ? (
                      <Select value={f.status} onValueChange={(v) => updateStatus(f, v)}>
                        <SelectTrigger className="h-7 w-[130px] text-[11px]" data-testid={`compliance-finding-status-select-${f.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={cn("inline-flex items-center gap-1 text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", meta.cls)}>
                        <meta.icon className="w-3 h-3" /> {meta.label}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <AddFindingModal open={addOpen} onClose={() => setAddOpen(false)} auditId={audit.id} onSaved={() => { setAddOpen(false); load(); onChanged?.(); }} />
      </DialogContent>
    </Dialog>
  );
}

export default function ComplianceAudits({ isAdmin, canEditFindings = isAdmin }) {
  const [audits, setAudits] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [openAuditId, setOpenAuditId] = useState(null);
  const [findingsAudit, setFindingsAudit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [auRes, fwRes] = await Promise.all([api.get("/compliance/audits"), api.get("/compliance/frameworks")]);
      setAudits(auRes.data.audits || []);
      setFrameworks(fwRes.data.frameworks || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  if (openAuditId) {
    return (
      <>
        <AuditReadiness
          auditId={openAuditId}
          isAdmin={isAdmin}
          frameworks={frameworks}
          onBack={() => setOpenAuditId(null)}
          onOpenFindings={() => setFindingsAudit(audits.find((a) => a.id === openAuditId))}
        />
        <FindingsDrawer audit={findingsAudit} isAdmin={canEditFindings} onClose={() => setFindingsAudit(null)} onChanged={load} />
      </>
    );
  }

  return (
    <div className="space-y-3" data-testid="compliance-audits-page">
      {isAdmin && <FindingActivityLog />}
      {isAdmin && (
        <div className="flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setModalOpen(true)} data-testid="compliance-audit-add-btn">
            <Plus className="w-3.5 h-3.5" /> Plan audit
          </Button>
        </div>
      )}
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table data-testid="compliance-audits-table">
          <TableHeader>
            <TableRow>
              <TableHead>Audit</TableHead>
              <TableHead>Framework</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Findings</TableHead>
              <TableHead>Open threads</TableHead>
              <TableHead>Period</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {audits.map((a) => (
              <TableRow
                key={a.id}
                className="cursor-pointer"
                onClick={() => setOpenAuditId(a.id)}
                data-testid={`compliance-audit-row-${a.id}`}
              >
                <TableCell className="text-[12.5px] font-medium">{a.name}</TableCell>
                <TableCell className="text-[11.5px] text-muted-foreground uppercase">{a.framework}</TableCell>
                <TableCell><span className="text-[10.5px] font-semibold rounded-full border border-primary/30 bg-primary/10 text-primary px-2 py-0.5">{a.stage}</span></TableCell>
                <TableCell className="text-[12px]">{a.type}</TableCell>
                <TableCell className="text-[12px]">{a.items}</TableCell>
                <TableCell className={cn("text-[12px]", a.findings > 0 && "text-red-500 font-semibold")}>{a.findings}{a.findings_total > a.findings ? ` / ${a.findings_total}` : ""}</TableCell>
                <TableCell className="text-[12px]">{a.open_threads}</TableCell>
                <TableCell className="text-[11.5px] text-muted-foreground">{a.period_start} – {a.period_end}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AddAuditModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} frameworks={frameworks} />
    </div>
  );
}
